import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@/primitives';
import {
  categoryCellSx,
  categoryRowEdges,
  type EbvGroupingOptions,
} from '@/hooks/publicSearch/ebv/ebvColumns';
import BreedAverageEbvCell from '@/components/publicSearch/animal/ebv/BreedAverageEbvCell';
import { EbvTraitHeaderLabel } from '@/components/publicSearch/animal/ebv/EbvTraitHeaderLabel';
import { coerceBreedAverageRaw } from '@/components/publicSearch/animal/ebv/breedAverageDisplay';
import type { EbvTraitHeaderContext } from '@/components/publicSearch/animal/ebv/ebvTraitExplanations';
import type { EbvAverageData, EbvRow } from './animalEbvTabTypes';

type AnimalEbvTabBreedAverageTableProps = {
  isMobile: boolean;
  ebvRows: EbvRow[];
  ebvAverageData: EbvAverageData;
  /** Society EBV label from setting 26 (e.g. EPD, EBV) — row caption only; cohort text lives above the table. */
  ebvLabel: string;
  currencySymbol: string;
  traitHeaderContext: EbvTraitHeaderContext;
  ebvGroupingOptions?: EbvGroupingOptions;
};

export default function AnimalEbvTabBreedAverageTable({
  isMobile,
  ebvRows,
  ebvAverageData,
  ebvLabel,
  currencySymbol,
  traitHeaderContext,
  ebvGroupingOptions,
}: AnimalEbvTabBreedAverageTableProps) {
  const breedAverageRowLabel = (
    <Typography
      variant="bodysmall"
      sx={{
        color: 'text.secondary',
      }}
    >
      {ebvLabel}
    </Typography>
  );

  return (
    <Table
      size="small"
      sx={{
        minWidth: { xs: 'auto', md: 900 },
        tableLayout: 'fixed',
        '& td:not(:first-of-type)': {
          whiteSpace: 'normal',
          verticalAlign: 'middle',
        },
        '& td:first-of-type': { whiteSpace: 'normal' },
      }}
    >
      {isMobile ? (
        <>
          <colgroup>
            <col style={{ width: 140 }} />
            <col style={{ width: 80 }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell align="center">
                <Typography
                  variant="bodysmall"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {ebvLabel}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ebvRows.map((ebvRow, i) => {
              const rawAvg = ebvAverageData?.[ebvRow.avgKey];
              const { isFirst } = categoryRowEdges(ebvRows, i);
              const cellSx = categoryCellSx(
                ebvRow.category,
                { top: isFirst },
                ebvGroupingOptions,
              );
              return (
                <TableRow key={ebvRow.key} hover>
                  <TableCell sx={cellSx}>
                    <EbvTraitHeaderLabel
                      label={ebvRow.label}
                      traitKey={ebvRow.traitKey}
                      traitHeaderContext={traitHeaderContext}
                      typographyProps={{ color: 'text.secondary' }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={cellSx}>
                    <BreedAverageEbvCell
                      raw={coerceBreedAverageRaw(rawAvg)}
                      format={ebvRow.format}
                      currencySymbol={currencySymbol}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </>
      ) : (
        <>
          <colgroup>
            <col style={{ width: 140 }} />
            {ebvRows.map((ebvRow) => (
              <col key={ebvRow.key} style={{ width: 86 }} />
            ))}
          </colgroup>
          <TableBody>
            <TableRow hover>
              <TableCell>{breedAverageRowLabel}</TableCell>
              {ebvRows.map((ebvRow, i) => {
                const rawAvg = ebvAverageData?.[ebvRow.avgKey];
                const { isFirst, isLast } = categoryRowEdges(ebvRows, i);
                return (
                  <TableCell
                    key={ebvRow.key}
                    align="center"
                    sx={categoryCellSx(
                      ebvRow.category,
                      { left: isFirst, right: isLast, top: true },
                      ebvGroupingOptions,
                    )}
                  >
                    <BreedAverageEbvCell
                      raw={coerceBreedAverageRaw(rawAvg)}
                      format={ebvRow.format}
                      currencySymbol={currencySymbol}
                    />
                  </TableCell>
                );
              })}
            </TableRow>
          </TableBody>
        </>
      )}
    </Table>
  );
}
